/**
 * League leaderboard page: paginated members via LeaderboardCard; shared lists via getMemberListForLeague.
 */
import { Box, Button, Center, Flex, Grid, GridItem, Text, Tooltip, VStack, useDisclosure, useToast } from '@chakra-ui/react';
import { type FunctionsError, httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import type { Country } from '@/models/country';
import { ACTIVE_TOURNAMENT, MAX_CHART_LEAGUE_SIZE } from '@/constants';
import { applyListSwap } from '@/helpers/roster/applyListSwap';
import { firebaseErrorToastOptions } from '@/helpers';
import { copyToClipboard, copyToClipboardAsync } from '@/helpers/clipboard';
import {
  getCompetingCount,
  getJoinOrderRank,
  getListPosition,
  isSettledLeagueRank,
} from '@/helpers/leagues/leagueMemberSync';
import { AuthedPageContainer } from '@/components';
import { FirebaseContext } from '@/session/FirebaseContext';
import { EditCountryListModal, ViewMemberListModal } from '@/components/modals';
import { useGameSession } from '@/session/useGameSession';
import { useUserProfile } from '@/session/useUserProfile';
import { sentinelType } from '@/models/gameUpdate';
import type { League } from '@/models';
import type { leagueMember } from '@/models/user';
import { pageButtonProps, pageTitleProps } from '@/theme/sitePageTypography';
import { LeaderboardCard } from './LeaderboardCard';
import { InviteCard } from './InviteCard';
import { MenuArea } from './MenuArea';
import { LeaguePageLayout } from './LeaguePageLayout';
import { LeaguePageHeader } from './LeaguePageHeader';
import { LeaguePageSkeleton } from './LeaguePageSkeleton';
import { LeagueDesktopDashboard } from './LeagueDesktopDashboard';
import { LeagueStandingsChartSection } from './LeagueStandingsChartSection';

export default function LeaguePage() {
  const {
    gameUpdates,
    leagues,
    memberData,
    tournamentStarted,
    leaguesHydrated,
    membersHydrated,
  } = useGameSession();
  const { userData, teamSelectionData } = useUserProfile();
  const latecomerAllowed = Object.keys(gameUpdates).includes(sentinelType.latecomersAllowed);
  const currentTournamentSelection = teamSelectionData[ACTIVE_TOURNAMENT] ?? {};
  const { countryList = [], latecomer = false, listIndexSwapPair = null } = currentTournamentSelection;

  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const ctx = useContext(FirebaseContext);
  const editCountryListDisclosure = useDisclosure();
  const viewMemberListDisclosure = useDisclosure();
  const [viewedMemberName, setViewedMemberName] = useState('Player');
  const [viewedMemberList, setViewedMemberList] = useState<Country[] | null>(null);
  const [viewedMemberListLoading, setViewedMemberListLoading] = useState(false);
  const [viewedMemberListError, setViewedMemberListError] = useState<string | null>(null);

  const [coldLoadLeague, setColdLoadLeague] = useState<(League & { id: string }) | undefined>();
  const [coldLoadMember, setColdLoadMember] = useState<leagueMember | undefined>();
  const [coldLoadAttempted, setColdLoadAttempted] = useState(false);

  // --- Data hooks: cold-load fallback when session not yet hydrated ---

  useEffect(() => {
    if (!id || !ctx.user || (leaguesHydrated && membersHydrated)) {
      return;
    }
    let cancelled = false;
    setColdLoadAttempted(false);
    void Promise.all([
      getDoc(doc(ctx.db, 'leagues', id)),
      getDoc(doc(ctx.db, 'leagues', id, 'members', ctx.user.uid)),
    ]).then(([leagueSnap, memberSnap]) => {
      if (cancelled) return;
      if (leagueSnap.exists()) {
        setColdLoadLeague({ ...(leagueSnap.data() as League), id: leagueSnap.id });
      }
      if (memberSnap.exists()) {
        setColdLoadMember(memberSnap.data() as leagueMember);
      }
      setColdLoadAttempted(true);
    });
    return () => {
      cancelled = true;
    };
  }, [ctx.db, ctx.user, id, leaguesHydrated, membersHydrated]);

  async function copyString(s: string) {
    try {
      await copyToClipboard(s);
      toast({
        title: 'Copied To Clipboard',
        status: 'success',
        duration: 2000,
      });
    } catch {
      toast({
        title: 'Failed To Copy',
        status: 'error',
        duration: 2000,
      });
    }
  }

  async function copyStandingsAsync(getText: () => Promise<string>) {
    try {
      const text = await copyToClipboardAsync(getText);
      if (!text.trim()) {
        toast({
          title: 'No standings to copy',
          description: 'No league members have submitted a team list yet.',
          status: 'info',
          duration: 3000,
        });
        return;
      }
      toast({
        title: 'Copied To Clipboard',
        status: 'success',
        duration: 2000,
      });
    } catch {
      toast({
        title: 'Failed To Copy',
        status: 'error',
        duration: 2000,
      });
    }
  }

  const contextReady = leaguesHydrated && membersHydrated;
  const leagueFromContext = id && contextReady && Object.keys(leagues).includes(id) ? leagues[id] : undefined;
  const memberFromContext = id && contextReady ? memberData[id] : undefined;
  const league = leagueFromContext ?? coldLoadLeague;
  const userMemberData = memberFromContext ?? coldLoadMember;

  if (!contextReady && !coldLoadAttempted) {
    return <LeaguePageSkeleton isGlobalLeague={id === 'global'} />;
  }

  if (!contextReady && coldLoadAttempted && (!league || !userMemberData)) {
    if (id === undefined) {
      return <LeaguePageSkeleton isGlobalLeague={false} />;
    }
    return (
      <AuthedPageContainer userData={userData}>
        <LeaguePageLayout>
          <Center py={16}>
            <VStack spacing={4}>
              <Text {...pageTitleProps}>League not found</Text>
              <Button {...pageButtonProps} as={Link} to='/profile' colorScheme='green'>
                Back to Game
              </Button>
              <Button {...pageButtonProps} variant='ghost' onClick={() => navigate('/rules', { replace: true })}>
                Home
              </Button>
            </VStack>
          </Center>
        </LeaguePageLayout>
      </AuthedPageContainer>
    );
  }

  if (contextReady && (id === undefined || !Object.keys(leagues).includes(id))) {
    return (
      <AuthedPageContainer userData={userData}>
        <LeaguePageLayout>
          <Center py={16}>
            <VStack spacing={4}>
              <Text {...pageTitleProps}>League not found</Text>
              <Button {...pageButtonProps} as={Link} to='/profile' colorScheme='green'>
                Back to Game
              </Button>
              <Button {...pageButtonProps} variant='ghost' onClick={() => navigate('/rules', { replace: true })}>
                Home
              </Button>
            </VStack>
          </Center>
        </LeaguePageLayout>
      </AuthedPageContainer>
    );
  }

  if (!league || !userMemberData || !id) {
    return <LeaguePageSkeleton isGlobalLeague={id === 'global'} />;
  }

  const isGlobalLeague = id === 'global';
  const showStandingsChart =
    !isGlobalLeague && getCompetingCount(league) <= MAX_CHART_LEAGUE_SIZE;
  const isAdmin = ctx.user!.uid === league.adminUID;
  const useActualRank = isSettledLeagueRank(userMemberData.currentRank);
  const userListPosition = getListPosition(league, userMemberData, ctx.user!.uid);
  const userRankToDisplay = useActualRank ? userMemberData.currentRank : getJoinOrderRank(league, userMemberData, ctx.user!.uid);

  const showPickTeams =
    countryList.length < 10 && (!tournamentStarted || (tournamentStarted && !!latecomer));

  const tooltipLabel = latecomerAllowed
    ? 'The tournament has now started. Changing teams will reset your points'
    : 'The tournament has now started and you can no longer pick new teams';

  async function handleViewMemberList(targetUid: string, member: leagueMember) {
    const displayName =
      member.name && member.name.trim().length > 0 ? member.name.trim() : 'Anonymous';
    setViewedMemberName(displayName);
    setViewedMemberList(null);
    setViewedMemberListError(null);
    viewMemberListDisclosure.onOpen();

    if (targetUid === ctx.user!.uid) {
      setViewedMemberList(applyListSwap(countryList, listIndexSwapPair));
      return;
    }

    setViewedMemberListLoading(true);
    try {
      const result = await httpsCallable(ctx.funcs, 'getMemberListForLeague')({
        leagueId: id,
        targetUid,
      });
      const data = result.data as {
        name: string;
        countryList: Country[];
        listIndexSwapPair: [number, number] | null;
      };
      setViewedMemberName(data.name);
      setViewedMemberList(applyListSwap(data.countryList, data.listIndexSwapPair));
    } catch (error) {
      const message =
        (error as FunctionsError).message ?? 'Could not load this player\'s list.';
      setViewedMemberListError(message);
      toast(firebaseErrorToastOptions('Could not load list', error as FunctionsError));
    } finally {
      setViewedMemberListLoading(false);
    }
  }

  // --- Leaderboard render + list/view modals ---

  const leagueMenuProps = {
    isAdmin,
    league,
    leagueId: id,
    db: ctx.db,
  };

  return (
    <AuthedPageContainer userData={userData}>
      <LeaguePageLayout wide={showStandingsChart}>
        <LeaguePageHeader
          leagueName={league.name}
          competingCount={getCompetingCount(league)}
          userMemberData={userMemberData}
          userRankToDisplay={userRankToDisplay}
          useActualRank={useActualRank}
          userListPosition={userListPosition}
          tournamentStarted={tournamentStarted}
        />

        {showPickTeams ? (
          <Tooltip hasArrow placement='top' label={tooltipLabel} isDisabled={!tournamentStarted}>
            <Button
              {...pageButtonProps}
              w='full'
              colorScheme='green'
              onClick={editCountryListDisclosure.onOpen}
              isDisabled={tournamentStarted && !latecomerAllowed}
            >
              Pick your teams
            </Button>
          </Tooltip>
        ) : null}

        {showStandingsChart ? (
          <>
            <Box display={{ base: 'none', lg: 'block' }} w='full'>
              <LeagueDesktopDashboard
                league={{ ...league, id }}
                isAdmin={isAdmin}
                isGlobalLeague={isGlobalLeague}
                tournamentStarted={tournamentStarted}
                copyStandingsAsync={copyStandingsAsync}
                onViewMemberList={(uid, member) => void handleViewMemberList(uid, member)}
                inviteId={id}
                copyString={copyString}
              />
            </Box>
            <Grid
              display={{ base: 'grid', lg: 'none' }}
              templateColumns='1fr'
              gap={4}
              w='full'
              alignItems='start'
            >
              <GridItem minW={0}>
                <LeaderboardCard
                  league={{ ...league, id }}
                  isAdmin={isAdmin}
                  isGlobalLeague={isGlobalLeague}
                  tournamentStarted={tournamentStarted}
                  copyStandingsAsync={copyStandingsAsync}
                  onViewMemberList={(uid, member) => void handleViewMemberList(uid, member)}
                />
              </GridItem>
              <GridItem minW={0}>
                <LeagueStandingsChartSection league={{ ...league, id }} />
              </GridItem>
              <GridItem minW={0}>
                <InviteCard id={id} isAdmin={isAdmin} copyString={copyString} league={league} />
              </GridItem>
            </Grid>
          </>
        ) : (
          <Grid templateColumns='1fr' gap={4} w='full' alignItems='start'>
            <GridItem minW={0}>
              <LeaderboardCard
                league={{ ...league, id }}
                isAdmin={isAdmin}
                isGlobalLeague={isGlobalLeague}
                tournamentStarted={tournamentStarted}
                copyStandingsAsync={copyStandingsAsync}
                onViewMemberList={(uid, member) => void handleViewMemberList(uid, member)}
              />
            </GridItem>
            {!isGlobalLeague ? (
              <GridItem minW={0}>
                <InviteCard id={id} isAdmin={isAdmin} copyString={copyString} league={league} />
              </GridItem>
            ) : null}
          </Grid>
        )}

        <Flex justify='center' w='full' pt={4}>
          <MenuArea {...leagueMenuProps} />
        </Flex>
      </LeaguePageLayout>

      <EditCountryListModal
        isOpen={editCountryListDisclosure.isOpen}
        onClose={editCountryListDisclosure.onClose}
        getStartingList={() => countryList}
        gameUpdates={gameUpdates}
      />
      <ViewMemberListModal
        isOpen={viewMemberListDisclosure.isOpen}
        onClose={viewMemberListDisclosure.onClose}
        onReopen={viewMemberListDisclosure.onOpen}
        memberName={viewedMemberName}
        countryList={viewedMemberList}
        loading={viewedMemberListLoading}
        error={viewedMemberListError}
      />
    </AuthedPageContainer>
  );
}
